import type { ReactNode } from 'react';
import { T, Var, useGT } from 'gt-react';
import type { CoreEnumDTO } from 'src/api/core/core-enums/useCoreEnums';
import { Label } from 'src/components/ui/label';
import {
  ClassificationCategoryFields,
  type InlineCategory,
} from './ClassificationCategoryFields';
import { Input } from 'src/components/ui/input';
import { Textarea } from 'src/components/ui/textarea';
import { Switch } from 'src/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  MultiSelect,
  type MultiSelectOption,
} from 'src/components/common/MultiSelect';
import { CLASSIFIABLE_FILE_EXTENSIONS } from '@erp/shared-types';

export function ClassificationFileTypeField({
  allowedFileExtensions,
  onChange,
  help,
}: {
  allowedFileExtensions: string[];
  onChange: (extensions: string[]) => void;
  help: ReactNode;
}) {
  const gt = useGT();
  const fileExtensionOptions: MultiSelectOption[] =
    CLASSIFIABLE_FILE_EXTENSIONS.map((fileType) => ({
      id: fileType.extension,
      label: fileType.label,
      group: fileType.group,
    }));
  const selectedExtensions: MultiSelectOption[] = allowedFileExtensions
    .map((ext) => fileExtensionOptions.find((option) => option.id === ext))
    .filter((option): option is MultiSelectOption => option !== undefined);

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">
        <T>Allowed File Types</T> <span className="text-destructive">*</span>
      </Label>
      <MultiSelect
        options={fileExtensionOptions}
        value={selectedExtensions}
        onChange={(selected) => onChange(selected.map((item) => item.id))}
        placeholder={gt('Select file types...', {
          context: 'file type multiselect placeholder',
        })}
        searchPlaceholder={gt('Search extensions...', {
          context: 'file extension search placeholder',
        })}
        fullWidth
        size="sm"
      />
      {allowedFileExtensions.length === 0 && (
        <p className="text-xs text-destructive">
          <T>At least one file type must be selected.</T>
        </p>
      )}
      {help}
    </div>
  );
}

export function ClassificationConfidenceField({
  confidenceThreshold,
  onChange,
  help,
}: {
  confidenceThreshold: number;
  onChange: (value: number) => void;
  help: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">
        <T context="confidence threshold label with percentage">
          Confidence Threshold (
          <Var>{Math.round(confidenceThreshold * 100)}</Var>%)
        </T>
      </Label>
      <Input
        type="number"
        min={0}
        max={1}
        step={0.05}
        value={confidenceThreshold}
        onChange={(e) => {
          const val = parseFloat(e.target.value);
          if (!isNaN(val) && val >= 0 && val <= 1) {
            onChange(val);
          }
        }}
      />
      {help}
    </div>
  );
}

export function ClassificationIncludeOtherField({
  includeOther,
  onChange,
  description,
}: {
  includeOther: boolean;
  onChange: (checked: boolean) => void;
  description: ReactNode;
}) {
  return (
    <div className="rounded-md border p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">
            <T>Include &quot;Other&quot; fallback</T>
          </p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <Switch checked={includeOther} onCheckedChange={onChange} />
      </div>
    </div>
  );
}

export function ClassificationAdvancedFields({
  classificationRules,
  baseProcessor,
  onClassificationRulesChange,
  onBaseProcessorChange,
  rulesPlaceholder,
  modelPlaceholder,
  rulesHelp,
}: {
  classificationRules: string;
  baseProcessor: string;
  onClassificationRulesChange: (value: string | undefined) => void;
  onBaseProcessorChange: (value: string | undefined) => void;
  rulesPlaceholder: string;
  modelPlaceholder: string;
  rulesHelp?: ReactNode;
}) {
  return (
    <details className="group" open>
      <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
        <T context="collapsible section label for advanced options">
          Advanced Options
        </T>
      </summary>
      <div className="mt-3 space-y-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            <T context="label for classification rules input">
              Classification Rules
            </T>
          </Label>
          <Textarea
            placeholder={rulesPlaceholder}
            value={classificationRules}
            onChange={(e) =>
              onClassificationRulesChange(e.target.value || undefined)
            }
            rows={3}
          />
          {rulesHelp}
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium">
            <T context="label for classification model selector">
              Classification Model
            </T>
          </Label>
          <Select
            value={baseProcessor}
            onValueChange={(value) => onBaseProcessorChange(value || undefined)}
          >
            <SelectTrigger>
              <SelectValue placeholder={modelPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="classification_performance">
                <T context="classification model option: high accuracy">
                  Performance (more accurate)
                </T>
              </SelectItem>
              <SelectItem value="classification_light">
                <T context="classification model option: lightweight">
                  Light (faster, cheaper)
                </T>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </details>
  );
}

export function ClassificationTrailingFields({
  variant,
  allowedFileExtensions,
  confidenceThreshold,
  includeOther,
  classificationRules,
  baseProcessor,
  onUpdate,
}: {
  variant: 'email' | 'document';
  allowedFileExtensions: string[];
  confidenceThreshold: number;
  includeOther: boolean;
  classificationRules: string;
  baseProcessor: string;
  onUpdate: (updates: Record<string, unknown>) => void;
}) {
  const gt = useGT();
  const isEmail = variant === 'email';

  return (
    <>
      {!isEmail && (
        <ClassificationFileTypeField
          allowedFileExtensions={allowedFileExtensions}
          onChange={(extensions) =>
            onUpdate({ allowedFileExtensions: extensions })
          }
          help={
            <p className="text-xs text-muted-foreground">
              <T>
                Documents with other extensions are classified as "other"
                without calling the provider.
              </T>
            </p>
          }
        />
      )}
      <ClassificationConfidenceField
        confidenceThreshold={confidenceThreshold}
        onChange={(value) => onUpdate({ confidenceThreshold: value })}
        help={
          <p className="text-xs text-muted-foreground">
            {isEmail ? (
              <T>
                Classifications below this confidence score are filtered out.
              </T>
            ) : (
              <T>
                Below this threshold, the document is classified as "other".
              </T>
            )}
          </p>
        }
      />
      <ClassificationIncludeOtherField
        includeOther={includeOther}
        onChange={(checked) => onUpdate({ includeOther: checked })}
        description={
          isEmail ? (
            <T>
              Automatically adds an &quot;other&quot; category for unmatched
              documents.
            </T>
          ) : (
            <T>Adds an "other" category for documents that don't match.</T>
          )
        }
      />
      <ClassificationAdvancedFields
        classificationRules={classificationRules}
        baseProcessor={baseProcessor}
        onClassificationRulesChange={(value) =>
          onUpdate({ classificationRules: value })
        }
        onBaseProcessorChange={(value) => onUpdate({ baseProcessor: value })}
        rulesPlaceholder={
          isEmail
            ? gt(
                "Natural language guidance for classification (e.g., 'Documents from ACME Corp are always rebate payments')",
                { context: 'classification rules textarea placeholder' },
              )
            : gt('Natural language guidance for classification', {
                context: 'classification rules textarea placeholder',
              })
        }
        modelPlaceholder={
          isEmail
            ? gt('Default (Performance)', {
                context: 'classification model selector placeholder',
              })
            : gt('Default (Performance)', {
                context: 'classification model select placeholder',
              })
        }
        rulesHelp={
          isEmail ? (
            <p className="text-xs text-muted-foreground">
              <T>Optional natural language rules to guide classification.</T>
            </p>
          ) : undefined
        }
      />
    </>
  );
}

export function ClassificationConfigForm({
  variant,
  classificationSource,
  coreEnumId,
  categories,
  enums,
  selectedEnum,
  allowedFileExtensions,
  confidenceThreshold,
  includeOther,
  classificationRules,
  baseProcessor,
  onUpdate,
  onClassificationSourceChange,
  onCoreEnumIdChange,
  children,
}: {
  variant: 'email' | 'document';
  classificationSource: string;
  coreEnumId: string;
  categories: InlineCategory[];
  enums: CoreEnumDTO[];
  selectedEnum: CoreEnumDTO | undefined;
  allowedFileExtensions: string[];
  confidenceThreshold: number;
  includeOther: boolean;
  classificationRules: string;
  baseProcessor: string;
  onUpdate: (updates: Record<string, unknown>) => void;
  onClassificationSourceChange: (source: string) => void;
  onCoreEnumIdChange: (id: string) => void;
  children?: ReactNode;
}) {
  return (
    <div className="space-y-5">
      <ClassificationCategoryFields
        classificationSource={classificationSource}
        coreEnumId={coreEnumId}
        categories={categories}
        enums={enums}
        selectedEnum={selectedEnum}
        onClassificationSourceChange={onClassificationSourceChange}
        onCoreEnumIdChange={onCoreEnumIdChange}
        onCategoriesChange={(next) => onUpdate({ categories: next })}
        enumHelp={
          variant === 'email' ? (
            <p className="text-xs text-muted-foreground">
              <T context="classify email config: enum options become categories and output sockets">
                Each enum option becomes a classification category and an output
                socket. Option name is used as the category name, description as
                the AI guidance.
              </T>
            </p>
          ) : undefined
        }
        inlineHelp={
          variant === 'email' ? (
            <p className="text-xs text-muted-foreground">
              <T context="classify email config: each inline category becomes an output socket">
                Each category becomes an output socket, so you can wire
                downstream steps per type. The existing category arrays stay
                available.
              </T>
            </p>
          ) : undefined
        }
      />
      {children}
      <ClassificationTrailingFields
        variant={variant}
        allowedFileExtensions={allowedFileExtensions}
        confidenceThreshold={confidenceThreshold}
        includeOther={includeOther}
        classificationRules={classificationRules}
        baseProcessor={baseProcessor}
        onUpdate={onUpdate}
      />
    </div>
  );
}
